import { Injectable } from '@angular/core';
import { OperationService } from '../operation/operation.service';
import { DailyLogEntriesService } from '../../daily-log/daily-log-entries/daily-log-entries.service';
import { DateParserService } from '../../daily-log/daily-log-time/date-parser-service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import {
  LocationChangeEditor,
  LocationOption,
  SetupChangeLogRecord,
  SetupChangeType,
  UpdatedSetupSummary
} from '../../../models/setup/setup.models';
import {
  DailyLogOperationInstance,
  DailyLogType,
  DailyOperationLogRecord,
  DailyOperationLogRecordHelper,
  TransitingHelper
} from '../../../models/projects/operation.models';
import { SetupService } from '../../setup/setup.service';
import { DailyLogService } from '../../daily-log/daily-log.service';
import { AppData, AppDataSummary } from '../../../models/app-data/app-data';
import { LocalSaveService } from '../../local/local-save.service';
import { cloneDeep } from 'lodash';
import { DateService } from '../../date/date.service';
import { TimeZoneService } from '../../time-zone/time-zone.service';
import { DateTime } from 'luxon';

@Injectable()
export class TransitingSaveService {

  constructor(
    public operationService: OperationService,
    public dailyLogEntryService: DailyLogEntriesService,
    public dateParserService: DateParserService,
    public dailyLogService: DailyLogService,
    public setupService: SetupService,
    private localSaveService: LocalSaveService,
    private dateService: DateService,
    private timeZoneService: TimeZoneService,
  ) {
  }

  async createLocationChangeEntry(
    transitingHelper: TransitingHelper,
    setupSummary: UpdatedSetupSummary,
    instance: DailyLogOperationInstance,
    locationOptions: LocationOption[],
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];
    helper.setupChangeLogRecords = [];
    const latestTime = this.operationService.getLatestTimeInOperationInstance(instance);
    const latestLocationSetupChange = await this.setupService.getSetupLocationForTime(latestTime);
    const locationName = locationOptions.find(e => e.id === instance.newLocationId).name;

    const uniqueOrdinal = await this.operationService.getDailyLogRecordUniqueOrdinal(1);
    setupSummary.locationName = locationName;
    setupSummary.locationId = instance.newLocationId;
    setupSummary.locationTimeChanged = new PtLuxonDate;

    const locationChangeEditor: LocationChangeEditor = {
      classification: 'Location',
      previousName: latestLocationSetupChange ? latestLocationSetupChange.newValue : 'No Location',
      newName: locationName,
      id: transitingHelper.id,
      uniqueOrdinal: uniqueOrdinal,
      type: DailyLogType.LocationChange,
      operationInstance: cloneDeep(instance)
    };

    const transitSetupId = await this.setupService.getSetupChangeLogRecordTempId();
    const transitingDailyLogRecord = transitingHelper.records.find(e => e.typeId === 10);
    if (transitingDailyLogRecord) {
      const transitingChange: SetupChangeLogRecord = {
        id: transitingDailyLogRecord.setupChangeLogApiId ? transitingDailyLogRecord.setupChangeLogApiId : null,
        localId: transitingDailyLogRecord.localSetupChangeLogRecordId ? transitingDailyLogRecord.localSetupChangeLogRecordId : null,
        changeType: SetupChangeType.Transiting,
        changeTypeId: SetupChangeType.Transiting,
        previousValue: latestLocationSetupChange ? latestLocationSetupChange.newValue : null,
        previousValueId: latestLocationSetupChange ? latestLocationSetupChange.newValueId : null,
        newValue: locationName,
        newValueId: instance.newLocationId,
        timeOfChange: instance.startTime,
        timeOfChangeToShow: instance.startTimeToShow,
        timeOfChangeOffset: instance.startTime.dateTime.offset / 60,
        dailyLogRecordId: transitingDailyLogRecord ? transitingDailyLogRecord.id : null,
        localDailyLogRecordId: transitingDailyLogRecord ? transitingDailyLogRecord.localId : null,
        batchId: null
      };

      await this.setupService.saveSetupChangeLogRecord(transitSetupId, transitingChange);
      helper.setupChangeLogRecords.push(transitingChange);
    }

    const locationSetupId = await this.setupService.getSetupChangeLogRecordTempId();
    const locationChange: SetupChangeLogRecord = {
      id: null,
      changeType: SetupChangeType.LocationChange,
      changeTypeId: SetupChangeType.LocationChange,
      previousValue: latestLocationSetupChange ? latestLocationSetupChange.newValue : null,
      previousValueId: latestLocationSetupChange ? latestLocationSetupChange.newValueId : null,
      newValue: locationName,
      newValueId: instance.newLocationId,
      timeOfChange: instance.endTime,
      timeOfChangeToShow: instance.endTimeToShow,
      timeOfChangeOffset: instance.endTime && instance.endTime.dateTime ? instance.endTime.dateTime.offset / 60 : null,
      dailyLogRecordId: null,
      localDailyLogRecordId: null,
      batchId: null
    };

    if (!!instance.newLocationId) {
      const locationChangeHelper = await this.dailyLogEntryService.createLocationChangeLogEntry(locationChangeEditor, locationSetupId, true);
      locationChange.localDailyLogRecordId = locationChangeHelper.records[0].localId;
      helper.records.push(locationChangeHelper.records[0]);
    } else {
      await this.dailyLogEntryService.updateLocationChangeTime(instance.id, instance.cancelledTime);
    }

    this.setupService.saveSetupChangeLogRecord(locationSetupId, locationChange);
    helper.setupChangeLogRecords.push(locationChange);

    if (!latestLocationSetupChange || instance.endTime.dateTime < DateTime.local()
      && instance.endTime.dateTime > latestLocationSetupChange.timeOfChange.dateTime) {
      this.setupService.saveSetup(setupSummary);
    }

    return helper;
  }

  async createBlankTransitingRecord(localId: number) {
    const transitingRecord = new SetupChangeLogRecord();

    transitingRecord.changeType = SetupChangeType.Transiting;
    transitingRecord.changeTypeId = SetupChangeType.Transiting;
    transitingRecord.localId = localId;
    return transitingRecord;
  }

  /* If a transit operation is added with no location change and the previous operation was a transit operation with a location change, then
  the location change record needs to be removed from the previous operation and only be applied after the final transit op has finished */
  async moveLocationChangeEntry(
    previousLocation: SetupChangeLogRecord,
    operationInstance: DailyLogOperationInstance
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];
    helper.setupChangeLogRecords = [];

    const uniqueOrdinal = await this.operationService.getDailyLogRecordUniqueOrdinal(1);

    const record =
      await this.dailyLogService.updatePreviousTransitingLocationChangeRecord(previousLocation, operationInstance, uniqueOrdinal);

    previousLocation = this.updateLocationChangeSetupRecord(previousLocation, operationInstance);
    helper.records.push(record);
    helper.setupChangeLogRecords.push(previousLocation);

    return helper;
  }

  async archiveLocationChangeEntry(previousLocation: SetupChangeLogRecord): Promise<DailyOperationLogRecord> {
    const previousLocationChangeRecord =
      await this.dailyLogService.getPreviousTransitingLocationChangeRecordByDailyLogId(
        previousLocation.dailyLogRecordId,
        previousLocation.localDailyLogRecordId);

    if (previousLocationChangeRecord) {
      previousLocationChangeRecord.archived = true;

      return previousLocationChangeRecord;
    }

    return null;
  }

  async unarchiveLocationChangeEntry(previousLocation: SetupChangeLogRecord): Promise<DailyOperationLogRecord> {
    const previousLocationChangeRecord =
      await this.dailyLogService.getPreviousTransitingLocationChangeRecordByDailyLogId(
        previousLocation.dailyLogRecordId,
        previousLocation.localDailyLogRecordId);

    if (previousLocationChangeRecord) {
      previousLocationChangeRecord.archived = false;

      return previousLocationChangeRecord;
    }

    return null;
  }

  updateTransitingToSetupRecord(
    transitingToRecord: SetupChangeLogRecord,
    operationInstance: DailyLogOperationInstance
  ): SetupChangeLogRecord {
    transitingToRecord.timeOfChange = operationInstance.startTime;
    transitingToRecord.timeOfChangeToShow = operationInstance.startTimeToShow;

    return transitingToRecord;
  }

  updateLocationChangeSetupRecord(
    locationChangeRecord: SetupChangeLogRecord,
    operationInstance: DailyLogOperationInstance,
    previousTransitingToRecord?: SetupChangeLogRecord,
    cancelledTimePtDate?: PtLuxonDate,
    cancelledTimeShow?: string
  ): SetupChangeLogRecord {
    if (previousTransitingToRecord) {
      locationChangeRecord.newValue = previousTransitingToRecord.newValue;
      locationChangeRecord.newValueId = previousTransitingToRecord.newValueId;
    }

    locationChangeRecord.timeOfChange = cancelledTimePtDate && cancelledTimePtDate.dateTime ? cancelledTimePtDate : operationInstance.endTime;
    locationChangeRecord.timeOfChangeToShow = cancelledTimeShow ? cancelledTimeShow : operationInstance.endTimeToShow;
    locationChangeRecord.timeOfChangeOffset = cancelledTimePtDate && cancelledTimePtDate.dateTime
      ? cancelledTimePtDate.dateTime.offset / 60
      : operationInstance.endTimeOffset;

    return locationChangeRecord;
  }

  /* If a transit operation is deleted with a location change - and there is a previous transit operation in the same 'transit', then  the
   location change record needs to be moved to the previous one, not deleted */
  async tryRevertLocationChangeEntry(locationChangeRecord: DailyOperationLogRecord): Promise<AppData> {
    const appDataBlob = new AppData();
    appDataBlob.setupChangeLogRecords = [];
    appDataBlob.dailyLogRecords = [];

    const locationChangeRecordOperation = await this.operationService.getOperationInstance(locationChangeRecord.operationInstanceId
      ? locationChangeRecord.operationInstanceId
      : locationChangeRecord.operationInstanceLocalId)
    // startTime.Date is local here
    const previousTransitingRecord = await this.dailyLogService.getPreviousTransitingToRecordByTime(locationChangeRecordOperation.startTime.dateTime);
    const previousTransitingSetupChangeRecord = previousTransitingRecord
      ? await this.setupService.getSetupChangeLogRecord(previousTransitingRecord.setupChangeLogApiId
        ? previousTransitingRecord.setupChangeLogApiId
        : previousTransitingRecord.localSetupChangeLogRecordId)
      : null;

    if (locationChangeRecord != null && previousTransitingRecord != null) {
      if (previousTransitingRecord.operationInstanceId !== locationChangeRecord.operationInstanceId) {
        // check if records are in different transits, if so dont move it back
        if (!(await this.checkDailyLogRecordsInSameTransit(locationChangeRecord, previousTransitingRecord))) {
          return null;
        }

        const record = await this.dailyLogService.moveLocationChangeRecordToLastTransitOperation(previousTransitingRecord, locationChangeRecord);
        const locationChangeSetupChangeRecord = await this.setupService.getSetupChangeLogRecord(locationChangeRecord.setupChangeLogApiId
          ? locationChangeRecord.setupChangeLogApiId
          : locationChangeRecord.localSetupChangeLogRecordId);

        // no need to resolve dates here, we just need the end date for relocating the location change record, but don't need to mutate it at all
        const operation = await this.operationService.getProjectSpecificOperationInstance(
          record.operationInstanceId
            ? record.operationInstanceId
            : record.operationInstanceLocalId);
        const locationChangeSetupRecord = this.updateLocationChangeSetupRecord(locationChangeSetupChangeRecord, operation, previousTransitingSetupChangeRecord);

        appDataBlob.dailyLogRecords.push(record);
        appDataBlob.setupChangeLogRecords.push(locationChangeSetupRecord);

        return appDataBlob;
      }
    }

    return null;
  }

  private async checkDailyLogRecordsInSameTransit(firstRecord: DailyOperationLogRecord, secondRecord: DailyOperationLogRecord) {
    if (!firstRecord.setupChangeLogApiId || !secondRecord.setupChangeLogApiId) {
      return false;
    }

    const firstRecordSetup = await this.setupService.getSetupChangeLogRecord(firstRecord.setupChangeLogApiId);
    const secondRecordSetup = await this.setupService.getSetupChangeLogRecord(secondRecord.setupChangeLogApiId);

    if (!firstRecordSetup || !secondRecordSetup) {
      return false;
    }

    return firstRecordSetup.transitEntityId == secondRecordSetup.transitEntityId;
  }

  async tryUnarchivePreviousLocationChangeRecord(row: DailyOperationLogRecord): Promise<AppData> {
    const appDataBlob = new AppData();
    appDataBlob.dailyLogRecords = [];

    if (row && row.operationClassification === 'Rig Move') {
      const previousLocation = await this.setupService.getLocationForTime(row.sortTime);

      if (previousLocation) {
        const locationChangeLogRecord = await this.unarchiveLocationChangeEntry(previousLocation);

        if (locationChangeLogRecord) {
          appDataBlob.dailyLogRecords.push(locationChangeLogRecord);
        }
      }
    }

    return appDataBlob;
  }

  async updateNextLocationChangeEntry(ptDate: PtLuxonDate, deletingCurrent?: boolean): Promise<AppData> {
    const currentLocationChange = await this.setupService.getLocationForTime(ptDate.dateTime);
    const nextLocationChange = await this.setupService.getNextLocationChange(currentLocationChange.timeOfChange);
    const nextLocationDailyLogRecord = await this.dailyLogService
      .getDailyLogRecordDirect(nextLocationChange.dailyLogRecordId
        ? nextLocationChange.dailyLogRecordId
        : nextLocationChange.localDailyLogRecordId) as DailyOperationLogRecord;
    const appDataBlob = new AppData();
    appDataBlob.setupChangeLogRecords = [];
    appDataBlob.dailyLogRecords = [];

    if (nextLocationDailyLogRecord == null) {
      return appDataBlob;
    }

    if (deletingCurrent) {
      const previousDate = ptDate.dateTime.minus({minute: 1}); // minus 1 minute from the given date
      const previousLocationChange = await this.setupService.getLocationForTime(previousDate);

      nextLocationChange.previousValue = previousLocationChange ? previousLocationChange.newValue : null;
      nextLocationChange.previousValueId = previousLocationChange ? previousLocationChange.newValueId : null;

      nextLocationDailyLogRecord.operationName = 'From '
      + previousLocationChange != null && previousLocationChange !== undefined
        ? previousLocationChange.newValue + ' To ' + nextLocationChange.newValue
        : 'No Location' + ' To ' + nextLocationChange.newValue;

      appDataBlob.setupChangeLogRecords.push(nextLocationChange);
      appDataBlob.dailyLogRecords.push(nextLocationDailyLogRecord);

      return appDataBlob;
    }

    if (nextLocationChange.newValueId === currentLocationChange.newValueId) {
      const deletionData = new AppDataSummary();
      deletionData.setupChangeLogRecordIds.push(nextLocationChange.id ? nextLocationChange.id : nextLocationChange.localId);
      deletionData.dailyLogRecordIds
        .push(nextLocationDailyLogRecord.id ? nextLocationDailyLogRecord.id : nextLocationDailyLogRecord.localId);
    } else {
      nextLocationChange.previousValue = currentLocationChange.newValue;
      nextLocationChange.previousValueId = currentLocationChange.newValueId;

      nextLocationDailyLogRecord.operationName = 'From ' + currentLocationChange.newValue + ' To ' + nextLocationChange.newValue;

      appDataBlob.setupChangeLogRecords.push(nextLocationChange);
      appDataBlob.dailyLogRecords.push(nextLocationDailyLogRecord);

      return appDataBlob;
    }
  }

  async getLinkedTransitingToChange(instance: DailyLogOperationInstance): Promise<DailyOperationLogRecord> {
    const logData = await this.dailyLogService.getDailyLogDataForOperation(instance.id);

    const transiting = logData
      .filter(e => e.typeId === DailyLogType.Transiting);

    if (transiting && transiting[0]) {
      return transiting[0];
    }

    return null;
  }

  async getLinkedLocationChange(instance: DailyLogOperationInstance): Promise<DailyOperationLogRecord> {
    const logData = await this.dailyLogService.getDailyLogDataForOperation(instance.id);

    const location = logData
      .filter(e => e.typeId === DailyLogType.LocationChange);

    if (location && location[0]) {
      return location[0];
    }

    return null;
  }

  async updateLinkedTransitingToEntry(instance: DailyLogOperationInstance): Promise<AppData> {
    const transitRecord = await this.getLinkedTransitingToChange(instance);

    if (transitRecord) {
      const appDataBlob = new AppData();

      appDataBlob.setupChangeLogRecords = [];
      appDataBlob.dailyLogRecords = [];

      transitRecord.startTime = instance.startTime;
      transitRecord.startTimeShow = instance.startTimeToShow;
      transitRecord.sortTime = instance.startTime.dateTime;
      transitRecord.operationStartTime = instance.startTime;

      appDataBlob.dailyLogRecords.push(transitRecord);

      const logData = await this.setupService.getSetupChangeLogData();

      const transit = logData
        .filter(e => e.dailyLogRecordId === transitRecord.id);

      if (transit && transit[0]) {
        transit[0].timeOfChange = transitRecord.startTime;
        transit[0].timeOfChangeToShow = transitRecord.startTimeShow;

        appDataBlob.setupChangeLogRecords.push(transit[0]);
      }

      return appDataBlob;
    }

    return null;
  }

  async updateLinkedLocationChangeEntry(instance: DailyLogOperationInstance): Promise<AppData> {
    const locationRecord = await this.getLinkedLocationChange(instance);

    if (locationRecord) {
      const appDataBlob = new AppData();

      appDataBlob.setupChangeLogRecords = [];
      appDataBlob.dailyLogRecords = [];

      locationRecord.startTime = instance.endTime;
      locationRecord.startTimeShow = instance.endTimeToShow;
      locationRecord.sortTime = instance.endTime.dateTime;
      locationRecord.operationStartTime = instance.startTime;

      appDataBlob.dailyLogRecords.push(locationRecord);

      const logData = await this.setupService.getSetupChangeLogData();

      const location = logData
        .filter(e => e.dailyLogRecordId === locationRecord.id);

      if (location && location[0]) {
        location[0].timeOfChange = locationRecord.startTime;
        location[0].timeOfChangeToShow = locationRecord.startTimeShow;

        appDataBlob.setupChangeLogRecords.push(location[0]);
      }

      return appDataBlob;
    }

    return null;
  }

  async getLocationChangeEntryId(operationInstance: DailyLogOperationInstance): Promise<number[]> {
    const records = [];

    const record = await this.dailyLogService.getDailyLogRecord(operationInstance.id, null, DailyLogType.LocationChange);
    const previousRecord = await this.dailyLogService.getPreviousTransitingLocationChangeRecordByOperation(operationInstance.id);

    if (record) {
      records.push(record.id ? record.id : record.localId);
    }

    if (previousRecord) {
      records.push(previousRecord.id ? previousRecord.id : previousRecord.localId);
    }

    return records;
  }

  async getTransitingToEntryId(operationInstance: DailyLogOperationInstance) {
    const record = await this.dailyLogService.getDailyLogRecord(operationInstance.id, null, DailyLogType.Transiting);

    if (record) {
      return record.id ? record.id : record.localId;
    }

    return null;
  }
}



// WEBPACK FOOTER //
// ./src/services/operations/transiting-save/transiting-save.service.ts